import React, { ReactElement } from 'react';

import { css, Global } from '@emotion/react';
import { defaultStyles } from '@hangar31/rc-layout';
import toColor from '@OnArk/utils/toColor';

import { OnArkTheme } from '../theme';

export default function GlobalStyled({
  theme,
}: {
  theme: OnArkTheme;
}): ReactElement {
  const b = toColor(theme?.church?.buttonColor || { b: 173, g: 39, r: 116 });

  return (
    <Global
      styles={[
        { button: theme.font('xs') },
        css`
          ${defaultStyles}

          html {
            position: relative;

            overflow: initial;
            --vw: 100vw;
          }

          body {
            font-family: ${theme.fontFamilies.Roboto};

            background-color: ${theme.colors.background};
          }

          a {
            color: ${theme.colors.clickable};
            font-weight: 400;
            font-family: 'Roboto';
            line-height: 2.4rem;
            text-decoration: none;

            &:hover {
              color: ${theme.colors.clickableLight};
            }
          }

          i {
            font-style: normal;
          }
          textarea {
            outline: none;
          }
          button {
            margin: 0;
            padding: 0;

            transition: all 0.2s;
          }

          select {
            padding-right: 2rem !important;

            background-image: url("data:image/svg+xml,%3Csvg height='40' width='40' viewBox='0 0 40 42' xmlns='http://www.w3.org/2000/svg' %3E%3Cg fill='none'%3E%3Cpath d='M21.414 22.252L20 23.666l-4.95-4.95 1.414-1.414 4.95 4.95z' fill='${b}' /%3E%3Cpath d='M20 23.666l-1.414-1.414 4.95-4.95 1.414 1.415-4.95 4.95z' fill='${b}' /%3E%3C/g%3E%3C/svg%3E");
            background-repeat: no-repeat;
            background-position: 99% 50%;
            cursor: pointer;

            appearance: none;
          }
          .react-datepicker__header__dropdown {
            margin-top: -4px;
            margin-bottom: 4px;
          }

          .react-datepicker__month-select,
          .react-datepicker__year-select {
            display: inline-block !important;
            align-items: center !important;
            box-sizing: border-box !important;
            margin: 0em !important;
            padding-right: initial !important;

            color: -internal-light-dark(black, white) !important;
            line-height: normal !important;
            letter-spacing: normal !important;
            white-space: pre !important;
            text-align: start !important;
            text-transform: none !important;
            text-indent: 0px !important;
            text-shadow: none !important;
            word-spacing: normal !important;

            background-color: -internal-light-dark(
              ${theme.colors.white},
              rgb(59, 59, 59)
            ) !important;
            background-image: none !important;
            border-width: 1px !important;
            border-style: solid !important;
            border-color: -internal-light-dark(
              rgb(118, 118, 118),
              rgb(133, 133, 133)
            ) !important;
            border-radius: 0px !important;
            border-image: initial !important;
            cursor: default !important;

            appearance: auto !important;
            writing-mode: horizontal-tb !important;
            text-rendering: auto !important;
            -webkit-rtl-ordering: logical !important;
          }
          .react-datepicker__triangle::after {
            border-bottom-color: #fff !important;
          }
          .react-datepicker__triangle::after,
          .react-datepicker__triangle::before {
            left: -1rem !important;
          }
          .react-datepicker__current-month {
            display: none;
          }

          .react-datepicker__header {
            background-color: ${theme.colors.whiteDark};
          }
          .react-datepicker__day--selected {
            background-color: ${toColor(theme.church.buttonColor)};
            &:hover {
              background-color: ${toColor(theme.church.buttonColor, 0.8)};
            }
          }

          .react-datepicker__navigation {
            width: 13px;
            height: 27px;
          }

          .react-datepicker__navigation-icon {
            position: static;
          }
          .react-datepicker__navigation-icon:before {
            width: 0;
            height: 0;

            border-width: 5px;

            border-color: transparent transparent transparent
              ${toColor(theme.church.buttonColor)};
            transform: none;
          }

          .react-datepicker__navigation-icon--next::before {
            right: 0;
            left: auto;
          }
          .react-datepicker__navigation-icon--previous::before {
            right: auto;
            left: 0;

            transform: rotate(180deg);
          }

          /* custom scrollbar */
          ::-webkit-scrollbar {
            width: 16px;
          }

          ::-webkit-scrollbar-track {
            background-color: transparent;
            border-left: 1px solid ${toColor(theme.colors.black, 0.1)};
          }

          ::-webkit-scrollbar-thumb {
            background-color: ${toColor(theme.colors.black, 0.15)};
            background-clip: content-box;
            border: 5px solid transparent;
            border-radius: 20px;
          }

          ::-webkit-scrollbar-thumb:hover {
            background-color: ${toColor(theme.colors.black, 0.25)};
          }
          .react-datepicker-wrapper {
            width: 100%;
          }
          .react-datepicker {
            font-weight: normal;
            font-size: 14px;
            font-family: ${theme.fontFamilies.Roboto};
          }

          .react-datepicker__day,
          .react-datepicker__day-name {
            width: 2.2rem;
          }

          .react-datepicker__current-month,
          .react-datepicker-time__header,
          .react-datepicker-year-header {
            font-size: 1.4rem;
          }
          .ql-align-right {
            text-align: right;
          }
          .ql-align-center {
            text-align: center;
          }
          .ql-align-left {
            text-align: left;
          }
          .sorting .dragHandle {
            cursor: grabbing;

            pointer-events: auto !important;
          }

          .error {
            position: absolute;
            top: calc(100% - 0.4rem);
            right: 0;

            display: inline-block;
            padding: 0.5rem;

            color: ${toColor(theme.church.buttonColor) ||
            theme.colors.highlight};
            font-size: 1rem;
          }
          label {
            position: relative;
          }

          .tippy-tooltip--big {
            font-size: 14px;
          }

          ._2NajI {
            max-height: 75vh !important;
          }
          /* rc-modal clamps its inline max-width to window.innerWidth,
             so on mobile every modal sits flush against both screen
             edges and the close button overhangs offscreen. Cap to a
             viewport-relative size below the md breakpoint so every
             modal keeps a 1rem margin around it on phones. Desktop
             modals keep their declared max-width. */
          @media (max-width: 991px) {
            ._2NajI {
              max-width: calc(100vw - 2rem) !important;
            }
          }
        `,
      ]}
    />
  );
}
